import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Send, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";
import DarkModeToggle from "../components/DarkModeToggle";

const Contact = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact/submit`, { name, email, subject, message });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sky-50 dark:bg-slate-900 relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-300/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-indigo-300/20 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")} data-testid="app-logo-home">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-lg sm:text-xl font-light text-sky-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
              {t("common.appName")}
            </span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <DarkModeToggle />
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sky-500 hover:text-sky-700 text-sm mb-6 transition-colors"
            data-testid="contact-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          {submitted ? (
            <div className="glass-card p-8 md:p-12 text-center" data-testid="contact-success">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-light text-sky-900 dark:text-white mb-2" style={{ fontFamily: "Outfit" }}>
                Message Sent!
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                Thank you for reaching out. We'll get back to you as soon as possible.
              </p>
              <Button onClick={() => navigate("/")} className="glow-button" data-testid="contact-back-home-btn">
                Back to Home
              </Button>
            </div>
          ) : (
            <div className="glass-card p-6 md:p-8" data-testid="contact-form-card">
              <h2 className="text-2xl font-light text-sky-900 dark:text-white mb-1" style={{ fontFamily: "Outfit" }}>
                Contact Us
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                Questions, partnership inquiries, or just want to say hi? We'd love to hear from you.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sky-700 dark:text-sky-300 text-sm">Name *</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="mt-1"
                    data-testid="contact-name-input"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sky-700 dark:text-sky-300 text-sm">Email *</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="mt-1"
                    data-testid="contact-email-input"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sky-700 dark:text-sky-300 text-sm">Subject</Label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="What's this about?"
                    className="mt-1"
                    data-testid="contact-subject-input"
                  />
                </div>
                <div>
                  <Label className="text-sky-700 dark:text-sky-300 text-sm">Message *</Label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us what's on your mind..."
                    className="mt-1 w-full min-h-[120px] rounded-md border border-sky-200 dark:border-slate-600 bg-white/80 dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400 resize-y"
                    data-testid="contact-message-input"
                    required
                    minLength={10}
                  />
                </div>
                <Button type="submit" className="w-full glow-button py-5" disabled={loading} data-testid="contact-submit-btn">
                  {loading ? (
                    <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" /> Send Message
                    </span>
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Contact;
